import { ChangeEvent, useMemo } from "react";

import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { resources } from "@constants/hardcoded-strings";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import { OrganizationDetailsMainValidationErrors } from "@idw-types/common/validation/organization/organization-details/organization-details-validation-errors";
import { OrganizationConfig } from "@idw-types/data/organization-config";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { CustomerSupportTier } from "@idw-types/data/reference-data/generic/customer-support-tier";
import { SelectOption } from "@idw-types/common";

type OrganizationDetailsGeneralTabProps = {
    isDisabled: boolean;
    canModifyCustomerSupportTier: boolean;
    organizationConfigState: OrganizationConfig;
    customerSupportTiers: Array<CustomerSupportTier>;
    setOrganizationConfigState: (organizationConfig: OrganizationConfig) => void;
    labelWidth: LabelWidthTypes;
    validationErrors: OrganizationDetailsMainValidationErrors;
}

export const OrganizationDetailsGeneralTab = (props: OrganizationDetailsGeneralTabProps) => {
    const {
        isDisabled,
        canModifyCustomerSupportTier,
        organizationConfigState,
        customerSupportTiers,
        labelWidth,
        validationErrors,
        setOrganizationConfigState
    } = props;

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOrganizationConfigState({
            ...organizationConfigState,
            name: event.target.value
        });
    };

    const handleCompanyCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOrganizationConfigState({
            ...organizationConfigState,
            companyCode: event.target.value
        });
    };

    const handleCustomerSupportTierChange = (selectedOption: SelectOption<string>) => {
        setOrganizationConfigState({
            ...organizationConfigState,
            customerSupportTierUID: selectedOption.value
        });
    }

    const customerSupportTierOptions = useMemo(() => {
        return [...customerSupportTiers]
            .sort((a, b) => parseInt(a.id) - parseInt(b.id))
            .map(customerTier => ({ label: customerTier.name, value: customerTier.uid }));
    }, [customerSupportTiers]);

    const selectedCustomerTierOption = useMemo(() => {
        if (organizationConfigState.customerSupportTierUID) {
            return customerSupportTierOptions.find(customerTier => customerTier.value === organizationConfigState.customerSupportTierUID);
        }

        return null;
    }, [customerSupportTierOptions, organizationConfigState.customerSupportTierUID]);

    const resource = resources.pages.organizationDetails.tabs.details;

    return (
        <div>
            <ContentPageHeader title={resources.pages.organizationDetails.tabs.details.title} />
            <ContentPageBody isMaxWidth={true}>
                <InputFormGroup
                    controlId="organization-name-input"
                    automationId={"organization-name-input"}
                    label={resources.pages.organizationDetails.tabs.details.inputs.organizationName.label}
                    ariaLabel={resources.pages.organizationDetails.tabs.details.inputs.organizationName.label}
                    value={organizationConfigState.name}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={resources.pages.organizationDetails.placeholders.organizationName}
                    onChange={handleNameChange}
                />
                {validationErrors.companyNameError}
                {canModifyCustomerSupportTier && (
                    <>
                        <InputFormGroup
                            controlId="organization-company-code-input"
                            automationId={"organization-company-code-input"}
                            label={resources.pages.organizationDetails.tabs.details.inputs.organizationCode.label}
                            ariaLabel={resources.pages.organizationDetails.tabs.details.inputs.organizationCode.label}
                            value={organizationConfigState.companyCode}
                            disabled={isDisabled}
                            isRequired={false}
                            labelWidth={labelWidth}
                            onChange={handleCompanyCodeChange}
                        />
                        {validationErrors.companyCodeError}
                    </>
                )}
                <SelectFormGroup
                    controlId={"custom-support-tier-select"}
                    automationId={"custom-support-tier-select"}
                    label={resource.inputs.customerSupportTier.label}
                    ariaLabel={resource.inputs.customerSupportTier.label}
                    options={customerSupportTierOptions}
                    selectedOption={selectedCustomerTierOption}
                    labelWidth={labelWidth}
                    isClearable={false}
                    isSearchable={false}
                    disabled={isDisabled || !canModifyCustomerSupportTier}
                    isRequired={false}
                    onChange={handleCustomerSupportTierChange}
                />
            </ContentPageBody>
        </div>
    );
}